import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { voteService } from 'services/api';
import { useMeeting } from 'context/MeetingContext';
import { useAuth } from 'context/AuthContext';
import TemporizadorVotacion from 'components/TemporizadorVotacion';
import { etiquetaEstado, etiquetaTipo } from 'utils/etiquetas';
import styles from './ResultsPage.module.css';

const POLL_INTERVAL = 3000;

// F-17 (b): el resultado de una sí/no se lee sobre la base de su regla (la
// decide el servidor, utils/decision.js): presentes, unidades o todo el conjunto.
const BASE_TXT = {
  coef_presentes:    'de los coeficientes presentes',
  unidades_presentes: 'de las unidades presentes',
  coef_conjunto:     'de los coeficientes del conjunto',
  unidades_conjunto: 'de las unidades del conjunto',
};
const textoVotos = (n) => `${n} ${Number(n) === 1 ? 'voto' : 'votos'}`;
const TIPO_TXT = { economica: 'Decisión económica', no_economica: 'Decisión no económica', calificada: 'Mayoría calificada (Art. 46)' };
const pctTxt = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 });
const textoDecision = (q) => (q.decision
  ? `${pctTxt(q.decision.pct_favor_base)}% a favor ${BASE_TXT[q.decision.base] || ''} (se requiere ${q.decision.tipo === 'calificada' ? '70 %' : 'más del 50 %'})`
  : `${q.pct_favor}% a favor vs ${q.pct_contra}% en contra`);

// ── Barra de progreso ─────────────────────────────────────────────────────────
function ProgressBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className={styles.barra}>
      <div className={styles.barraCabecera}>
        <span className={styles.barraEtiqueta}>{label}</span>
        <div className={styles.barraDatos}>
          <span className={styles.barraVotos}>{textoVotos(value)}</span>
          <span className={styles.barraPct} style={{ '--tono': color }}>{pct}%</span>
        </div>
      </div>
      <div className={styles.barraPista} aria-hidden="true">
        <div className={styles.barraRelleno} style={{ '--ancho': `${pct}%`, '--tono': color }} />
      </div>
    </div>
  );
}

// ── Gauge de quórum (usa datos reales de asistentes) ──────────────────────────
function QuorumGauge({ quorumInfo, quorumConfig }) {
  const pct     = quorumInfo?.pct_asistencia ?? quorumConfig ?? 0;
  const req     = quorumInfo?.quorum_requerido ?? quorumConfig ?? 51;
  const reached = quorumInfo?.quorum_alcanzado ?? (pct >= req);
  const capped  = Math.min(pct, 100);
  const radius  = 54;
  const circ    = 2 * Math.PI * radius;
  const dash    = (capped / 100) * circ;
  const color   = reached ? 'var(--success)' : pct >= req * 0.7 ? '#f59e0b' : '#ff6b6b';

  return (
    <div className={styles.medidor}>
      {/* Círculo */}
      <div className={styles.medidorCirculo}>
        <svg width="140" height="140" className={styles.medidorSvg} aria-hidden="true">
          <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--bg3)" strokeWidth="12" />
          <circle cx="70" cy="70" r={radius} fill="none" stroke={color} strokeWidth="12"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            className={styles.medidorArco} />
        </svg>
        <div className={styles.medidorCentro}>
          <span className={styles.medidorPct} style={{ '--tono': color }}>{pct}%</span>
          <span className={styles.medidorSub}>asistencia</span>
        </div>
      </div>

      {/* Detalle */}
      <div className={styles.medidorDetalle}>
        {quorumInfo ? (
          <>
            <div className={styles.medidorRejilla}>
              {[
                { label: 'Convocados',        value: quorumInfo.total_convocados },
                { label: 'Presentes',         value: quorumInfo.total_presentes },
                { label: 'Quórum requerido',  value: `${req}%` },
                // #13: antes "Mínimo (50+1)" contaba PERSONAS; el cuórum es por coeficiente.
                { label: 'Falta para el quórum', value: reached ? '✓ Alcanzado' : `${Math.max(0, req - pct).toFixed(1)} pts` },
              ].map(item => (
                <div key={item.label} className={styles.medidorDato}>
                  <div className={styles.medidorDatoEtiqueta}>{item.label}</div>
                  <div className={styles.medidorDatoValor}>{item.value}</div>
                </div>
              ))}
            </div>
            <div className={`${styles.medidorEstado} ${reached ? styles.medidorEstadoOk : styles.medidorEstadoAviso}`}>
              {reached
                ? <><i className={`fas fa-check-circle ${styles.icono8}`} />Quórum alcanzado — la asamblea puede tomar decisiones válidas</>
                : <><i className={`fas fa-exclamation-circle ${styles.icono8}`} />Quórum insuficiente — se requiere {req}% ({quorumInfo.quorum_minimo} presentes mínimo)</>
              }
            </div>
          </>
        ) : (
          <p className={styles.sinDatos}>Sin datos de asistencia registrados.</p>
        )}
      </div>
    </div>
  );
}

// ── Barra de opción múltiple ──────────────────────────────────────────────────
function OptionBar({ label, votes, pct, color = 'var(--accent)', isWinner }) {
  return (
    <div className={styles.opcion}>
      <div className={styles.opcionCabecera}>
        <span className={`${styles.opcionEtiqueta} ${isWinner ? styles.opcionEtiquetaGanadora : ''}`}>
          {isWinner && <i className={`fas fa-trophy ${styles.trofeo}`} />}
          {label}
        </span>
        <div className={styles.opcionDatos}>
          <span className={styles.opcionVotos}>{votes} voto{votes !== 1 ? 's' : ''}</span>
          <span className={styles.opcionPct} style={{ '--tono': color }}>{pct}%</span>
        </div>
      </div>
      <div className={styles.opcionPista} aria-hidden="true">
        <div className={`${styles.opcionRelleno} ${isWinner ? styles.opcionRellenoGanadora : ''}`}
          style={{ '--ancho': `${pct}%`, '--tono': color, '--halo': `${color}66` }} />
      </div>
    </div>
  );
}

// Paleta de colores para opciones múltiples
const OPTION_COLORS = ['#6c63ff', '#12b76a', '#f79009', '#f04438', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6'];

// Helper que renderiza barras según question_type
function QuestionBars({ q }) {
  if (q.question_type === 'yes_no') {
    return (
      <>
        <ProgressBar label="A favor"    value={q.favor}      total={q.total_votes} color="var(--success)" />
        <ProgressBar label="En contra"  value={q.contra}     total={q.total_votes} color="#ff6b6b" />
        <ProgressBar label="Abstención" value={q.abstencion} total={q.total_votes} color="var(--text-muted)" />
      </>
    );
  }
  if (!q.option_results || q.option_results.length === 0)
    return <p className={styles.sinDatos}>Sin opciones registradas</p>;

  const maxPct = Math.max(...q.option_results.map(o => o.pct));
  return (
    <>
      {q.option_results.map((opt, i) => (
        <OptionBar
          key={opt.label}
          label={opt.label}
          votes={opt.votes}
          pct={opt.pct}
          color={OPTION_COLORS[i % OPTION_COLORS.length]}
          isWinner={opt.pct === maxPct && opt.pct > 0}
        />
      ))}
    </>
  );
}

// ── MODO PROYECCIÓN ─────────────────────────────────────────────────────────
function ProjectionMode({ data, onExit }) {
  const qi      = data?.quorum_info;
  const pct     = qi?.pct_asistencia ?? 0;
  const req     = qi?.quorum_requerido ?? data?.assembly?.quorum ?? 51;
  const reached = qi?.quorum_alcanzado ?? false;

  const activeQs = (data?.questions || []).filter(q => q.active);
  // Preguntas cerradas ordenadas de la última creada a la primera (mayor id primero)
  const closedQs = (data?.questions || [])
    .filter(q => !q.active && q.total_votes > 0)
    .sort((a, b) => b.id - a.id);
  const showing  = activeQs.length > 0 ? activeQs : closedQs.slice(0, 1);

  const qColor  = reached ? '#12b76a' : pct >= req * 0.7 ? '#f79009' : '#f04438';

  return (
    <div className={styles.proy}>

      {/* ── Topbar ── */}
      <div className={styles.proyBarra}>
        {/* Marca + asamblea */}
        <div className={styles.proyMarca}>
          <div className={styles.proyLogo}>
            <i className={`fas fa-vote-yea ${styles.proyLogoIcono}`} />
          </div>
          <div>
            <div className={styles.proyNombre}>
              {data?.assembly?.property?.name}
            </div>
            <div className={styles.proyAsamblea}>
              Asamblea #{data?.assembly?.number_assembly}
              {data?.assembly?.assembly_type ? ` · ${etiquetaTipo(data.assembly.assembly_type)}` : ''}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className={styles.proyKpis}>
          {/* Asistentes */}
          <div className={styles.proyAsistencia}>
            <div className={styles.proyAsistenciaEtiqueta}>Asistencia</div>
            <div className={styles.proyAsistenciaPct} style={{ '--tono': qColor }}>
              {pct}%
            </div>
            {qi && (
              <div className={styles.proyAsistenciaConteo}>
                {qi.total_presentes}/{qi.total_convocados}
              </div>
            )}
          </div>

          {/* Badge quórum */}
          <div className={`${styles.proyQuorum} ${reached ? styles.proyQuorumOk : styles.proyQuorumPendiente}`}>
            <i className={`fas ${reached ? 'fa-circle-check' : 'fa-clock'}`} />
            {reached ? `Quórum ✓ (${req}%)` : `Requiere ${req}%`}
          </div>

          <div className={styles.proySeparador} />

          <button onClick={onExit} className={styles.proySalir}>
            <i className="fas fa-compress" />Salir
          </button>
        </div>
      </div>

      {/* ── Contenido ── */}
      <div className={styles.proyContenido}>
        {showing.length === 0 ? (
          <div className={styles.proyVacio}>
            <div className={styles.proyVacioCirculo}>
              <i className={`fas fa-hourglass-half ${styles.proyVacioIcono}`} />
            </div>
            <p className={styles.proyVacioTitulo}>Esperando votaciones abiertas...</p>
            <p className={styles.proyVacioNota}>El administrador abrirá las votaciones cuando corresponda</p>
          </div>
        ) : (
          showing.map((q) => {
            const total = q.total_votes;

            const projBars = q.question_type === 'yes_no'
              ? [
                  { label: 'A FAVOR',    value: q.favor,      pct: q.pct_favor,      color: '#12b76a', icon: 'fa-thumbs-up' },
                  { label: 'EN CONTRA',  value: q.contra,     pct: q.pct_contra,     color: '#f04438', icon: 'fa-thumbs-down' },
                  { label: 'ABSTENCIÓN', value: q.abstencion, pct: q.pct_abstencion, color: '#8a92a6', icon: 'fa-minus' },
                ]
              : (q.option_results || []).map((opt, i) => ({
                  label: opt.label, value: opt.votes, pct: opt.pct,
                  color: OPTION_COLORS[i % OPTION_COLORS.length], icon: 'fa-circle',
                }));

            const winner = projBars.length > 0
              ? projBars.reduce((a, b) => a.pct >= b.pct ? a : b)
              : null;
            // Sí/no: el resultado es aprobada / no aprobada con la regla del acta
            // (`approved` del servidor), no la barra más alta — si ganaba la
            // abstención se leía "ABSTENCIÓN — 60%" como resultado.
            const esSiNo    = q.question_type === 'yes_no';
            const resultado = esSiNo
              ? { color: q.approved ? '#12b76a' : '#f04438',
                  texto: `${q.active ? (q.approved ? 'Se aprobaría' : 'No se aprobaría') : (q.approved ? 'APROBADA' : 'NO APROBADA')} · ${textoDecision(q)}` }
              : winner && { color: winner.color, texto: `${winner.label} — ${winner.pct}%` };

            return (
              <div key={q.id} className={styles.proyPregunta}>
                {/* Badge + título */}
                <div className={styles.proyPreguntaCabecera}>
                  <span className={`${styles.proyEstado} ${q.active ? styles.proyEstadoActiva : styles.proyEstadoCerrada}`}>
                    <i className={`fas fa-circle ${styles.proyEstadoPunto}`} />
                    {q.active ? 'EN VOTACIÓN' : 'CERRADA'}
                  </span>
                  <span className={styles.proyVotos}>
                    {total} voto{total !== 1 ? 's' : ''} registrado{total !== 1 ? 's' : ''}
                  </span>
                  {q.active && <TemporizadorVotacion preguntaId={q.id} variante="proyeccion" />}
                </div>

                <h2 className={styles.proyTitulo}>
                  {q.question_text}
                </h2>

                {/* Barras */}
                <div className={styles.proyBarras}>
                  {projBars.map(opt => {
                    const isWinner = total > 0 && winner && opt.pct === winner.pct && opt.pct > 0;
                    return (
                      <div key={opt.label} style={{ '--tono': opt.color, '--fondo': opt.color + '18', '--borde': `${opt.color}33`, '--halo': `${opt.color}55`, '--tonocc': `${opt.color}cc`, '--ancho': `${opt.pct}%` }}>
                        <div className={styles.proyBarraCabecera}>
                          <div className={styles.proyBarraIzq}>
                            <div className={styles.proyBarraIcono}>
                              <i className={`fas ${opt.icon} ${styles.proyBarraGlifo}`} />
                            </div>
                            <span className={`${styles.proyBarraNombre} ${isWinner ? styles.proyBarraNombreGanadora : ''}`}>
                              {opt.label}
                            </span>
                            {isWinner && total > 0 && (
                              <span className={styles.proyMayoria}>
                                MAYORÍA
                              </span>
                            )}
                          </div>
                          <div className={styles.proyBarraDer}>
                            <span className={styles.proyVotos}>{textoVotos(opt.value)}</span>
                            <span className={styles.proyPct}>
                              {opt.pct}%
                            </span>
                          </div>
                        </div>
                        <div className={styles.proyPista} aria-hidden="true">
                          <div className={`${styles.proyRelleno} ${isWinner ? styles.proyRellenoGanadora : ''}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Resultado */}
                {total > 0 && resultado && (
                  <div className={styles.proyResultado} style={{ '--tono': resultado.color, '--fondo': resultado.color + '0d', '--borde': `${resultado.color}33` }}>
                    <span className={styles.proyResultadoEtiqueta}>
                      {q.active ? 'Tendencia actual' : 'Resultado final'}
                    </span>
                    <strong className={styles.proyResultadoTexto}>
                      {resultado.texto}
                    </strong>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Footer ── */}
      <div className={styles.proyPie}>
        <span className={styles.proyPieMarca}>
          EVENTIFY · Resultados en vivo
        </span>
        <span className={styles.proyPieEstado}>
          <span className={styles.proyPieLuz} />
          Actualizando cada {POLL_INTERVAL / 1000}s
        </span>
      </div>
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────────
export default function ResultsPage() {
  const { assemblyId }              = useParams();
  const { isAssemblyAdmin }         = useAuth();
  const { abrirReunion }            = useMeeting();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [pulse,      setPulse]      = useState(false);
  const [projection, setProjection] = useState(false);
  const [sinAcceso,  setSinAcceso]  = useState(false);   // 403: la asamblea existe pero esta cuenta no puede verla

  // Devuelve true si conviene seguir consultando. 404 (la asamblea no existe) y 403 (sin acceso) son permanentes: reintentar cada 3 s
  // para siempre solo gastaba red (una pestaña olvidada en /results/abc consultaba sin parar). Cualquier otro error (red, 500, 503) es
  // momentáneo: se sigue consultando para recuperarse solo.
  const load = useCallback(() => voteService.results(assemblyId)
    .then(r => {
      setData(r.data);
      setSinAcceso(false);
      setLoading(false);
      setLastUpdate(new Date());
      setPulse(true);
      setTimeout(() => setPulse(false), 400);
      return true;
    })
    .catch(err => {
      setLoading(false);
      const estado = err?.response?.status;
      if (estado !== 403 && estado !== 404) return true;
      setData(null);   // lo que se veía ya no es de esta asamblea: no se deja un número viejo como si fuera en vivo
      setSinAcceso(estado === 403);
      return false;
    }), [assemblyId]);

  useEffect(() => {
    let interval = null;
    const ciclo = () => load().then(seguir => { if (!seguir && interval) { clearInterval(interval); interval = null; } });
    ciclo();
    interval = setInterval(ciclo, POLL_INTERVAL);
    return () => { if (interval) clearInterval(interval); };
  }, [load]);

  const activeQuestions   = (data?.questions || []).filter(q => q.active);
  const closedQuestions   = (data?.questions || []).filter(q => !q.active && q.total_votes > 0).sort((a, b) => b.id - a.id);
  const inactiveQuestions = (data?.questions || []).filter(q => !q.active && q.total_votes === 0);
  // Mapa id → número de pregunta según orden de creación (id asc)
  const questionNumMap = Object.fromEntries(
    [...(data?.questions || [])].sort((a, b) => a.id - b.id).map((q, i) => [q.id, i + 1])
  );
  const totalVotesAll     = (data?.questions || []).reduce((s, q) => s + q.total_votes, 0);

  const statusColor = { en_progreso: 'var(--success)', pendiente: '#f59e0b', finalizada: 'var(--text-muted)' };

  if (projection && data) return <ProjectionMode data={data} onExit={() => setProjection(false)} />;

  return (
    <div className={styles.pagina}>
      <div className={styles.contenedor}>

        {/* Header */}
        <div className={styles.cabecera}>
          <div>
            <h1 className={styles.titulo}>
              <i className="fas fa-chart-bar" />
              Resultados en vivo
              <span className={`${styles.pulso} ${pulse ? styles.pulsoActivo : ''}`} aria-hidden="true" />
            </h1>
            {data && (
              <p className={styles.subtitulo}>
                Asamblea #{data.assembly.number_assembly} — {data.assembly.property?.name}
              </p>
            )}
          </div>
          <div className={styles.acciones}>
            {lastUpdate && (
              <span className={styles.hora}>
                <i className={`fas fa-sync ${styles.icono4}`} />{lastUpdate.toLocaleTimeString('es-CO')}
              </span>
            )}
            {isAssemblyAdmin && (
              <button className={`btn btn-primary ${styles.botonProyectar}`} onClick={() => setProjection(true)}>
                <i className="fas fa-expand" /> Proyectar
              </button>
            )}
            {['virtual','hybrid'].includes(data?.assembly?.assembly_model) && data?.assembly?.zoom_meeting_number && (
              <button className={`btn btn-primary ${styles.botonReunion}`}
                onClick={() => abrirReunion({
                  assemblyId:   data.assembly.id,
                  assemblyName: `${data.assembly.property?.name || 'Asamblea'} #${data.assembly.number_assembly}`,
                  isModerator:  isAssemblyAdmin,
                })}>
                <i className="fas fa-video" /> Reunión virtual
              </button>
            )}
            <Link to="/dashboard" className={`btn btn-secondary ${styles.botonPanel}`}>
              <i className="fas fa-arrow-left" /> Panel
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /><p>Cargando resultados...</p></div>
        ) : !data ? (
          <div className={`card ${styles.vacio}`}>
            <p className={styles.textoMudo}>{sinAcceso ? 'No tienes acceso a esta asamblea' : 'Asamblea no encontrada'}</p>
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className={styles.kpis}>
              {[
                { label: 'Estado',             value: etiquetaEstado(data.assembly.status), icon: 'fa-circle',          color: statusColor[data.assembly.status] || 'var(--text-muted)' },
                { label: 'Tipo',               value: etiquetaTipo(data.assembly.assembly_type), icon: 'fa-building',        color: 'var(--accent)' },
                { label: 'Votaciones abiertas',  value: activeQuestions.length,      icon: 'fa-question-circle', color: 'var(--accent2)' },
                { label: 'Total votos',        value: totalVotesAll,               icon: 'fa-poll',            color: 'var(--success)' },
              ].map(c => (
                <div key={c.label} className={`card ${styles.kpiTarjeta}`}>
                  <div className={styles.kpiFila}>
                    <div className={styles.kpiIcono}>
                      <i className={`fas ${c.icon} ${styles.kpiGlifo}`} style={{ '--tono': c.color }} />
                    </div>
                    <div>
                      <div className={styles.kpiEtiqueta}>{c.label}</div>
                      <div className={styles.kpiValor}>{c.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quórum — basado en asistentes reales */}
            <div className={`card ${styles.quorum}`}>
              <h2 className={styles.quorumTitulo}>
                <i className={`fas fa-users ${styles.icono8}`} />Quórum de la asamblea
              </h2>
              <QuorumGauge quorumInfo={data.quorum_info} quorumConfig={data.assembly.quorum} />
            </div>

            {/* Preguntas activas */}
            {activeQuestions.length > 0 && (
              <div className={styles.seccion}>
                <h2 className={styles.seccionTituloActiva}>
                  <i className={`fas fa-circle ${styles.puntoSeccion}`} />Preguntas en votación
                </h2>
                {activeQuestions.map((q, i) => (
                  <div className={`card ${styles.preguntaActiva}`} key={q.id} role="group" aria-labelledby={`pregunta-${q.id}`}>
                    <div className="card-header">
                      <div>
                        <span className={styles.preguntaNumero}>Pregunta {i + 1}</span>
                        <h3 className={styles.preguntaTitulo} id={`pregunta-${q.id}`}>{q.question_text}</h3>
                      </div>
                      <div className={styles.preguntaLado}>
                        <span className="badge badge-success"><i className={`fas fa-circle ${styles.puntoBadge}`} />En votación</span>
                        <span className={styles.preguntaVotos}>{textoVotos(q.total_votes)}</span>
                        <TemporizadorVotacion preguntaId={q.id} variante="panel" conAcciones />
                      </div>
                    </div>
                    <div className={styles.cuerpoActiva}>
                      {q.total_votes === 0 ? (
                        <p className={styles.esperando}>
                          <i className={`fas fa-hourglass-half ${styles.icono8}`} />Esperando votos...
                        </p>
                      ) : (
                        <>
                          <QuestionBars q={q} />
                          {q.question_type === 'yes_no' && (
                            <div className={`${styles.tendencia} ${styles.tendenciaActiva}`}>
                              <span className={styles.tendenciaEtiqueta}>{TIPO_TXT[q.tipo_decision] || 'Tendencia actual'}</span>
                              <strong className={q.approved ? styles.veredictoOk : styles.veredictoNo}>
                                {q.approved ? '✓ Se aprobaría' : '✗ No se aprobaría'} · {textoDecision(q)}
                              </strong>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Preguntas cerradas */}
            {closedQuestions.length > 0 && (
              <div className={styles.seccionCorta}>
                <h2 className={styles.seccionTitulo}>
                  <i className={`fas fa-lock ${styles.icono8}`} />Preguntas cerradas
                </h2>
                {closedQuestions.map((q) => (
                  <div className={`card ${styles.preguntaCerrada}`} key={q.id} role="group" aria-labelledby={`pregunta-${q.id}`}>
                    <div className="card-header">
                      <div>
                        <span className={styles.preguntaNumero}>Pregunta {questionNumMap[q.id] || '—'}</span>
                        <h3 className={styles.preguntaTituloCerrada} id={`pregunta-${q.id}`}>{q.question_text}</h3>
                      </div>
                      <div className={styles.preguntaLado}>
                        <span className="badge badge-secondary">Cerrada</span>
                        <span className={styles.preguntaVotos}>{textoVotos(q.total_votes)}</span>
                      </div>
                    </div>
                    {q.total_votes > 0 && (
                      <div className={styles.cuerpoCerrada}>
                        <QuestionBars q={q} />
                        {q.question_type === 'yes_no' && (
                          <div className={`${styles.tendencia} ${styles.tendenciaCerrada}`}>
                            <span className={styles.tendenciaEtiqueta}>{TIPO_TXT[q.tipo_decision] || 'Resultado final'}</span>
                            {/* `approved` viene del servidor: misma regla que el acta */}
                            <strong className={q.approved ? styles.veredictoOk : styles.veredictoNo}>
                              {q.approved ? '✓ Aprobada' : '✗ No aprobada'} · {textoDecision(q)}
                            </strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Preguntas inactivas */}
            {inactiveQuestions.length > 0 && (
              <div className={styles.seccionCorta}>
                <h2 className={styles.seccionTitulo}>
                  <i className={`fas fa-circle ${styles.icono8Tenue}`} />Preguntas inactivas
                </h2>
                {inactiveQuestions.map(q => (
                  <div className={`card ${styles.preguntaInactiva}`} key={q.id} role="group" aria-labelledby={`pregunta-${q.id}`}>
                    <div className={`card-header ${styles.cabeceraInactiva}`}>
                      <h3 className={styles.preguntaTituloInactiva} id={`pregunta-${q.id}`}>{q.question_text}</h3>
                      <span className={styles.chipCerrada}>
                        Cerrada
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {data.questions.length === 0 && (
              <div className={`card ${styles.vacio}`}>
                <i className={`fas fa-question-circle ${styles.vacioIcono}`} />
                <h2 className={styles.vacioTitulo}>Sin preguntas aún</h2>
                <p className={styles.textoMudo}>El administrador no ha creado preguntas para esta asamblea.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}